if (!customElements.get('hotspot-slider')) {
  customElements.define(
    'hotspot-slider',
    class HotspotSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.isDesktop = window.matchMedia('(hover: hover)');
        this.boundCloseAll = this.closeAllTooltips.bind(this);
        this.boundOnKeydown = this.onKeydown.bind(this);
        this._tooltipMap = new Map();

        // Portal container: direct child of hotspot-slider (no overflow clipping)
        this._tooltipPortal = document.createElement('div');
        this._tooltipPortal.className = 'hotspot-tooltip-portal';
        this.appendChild(this._tooltipPortal);

        // Defer so children are guaranteed to be in the DOM
        requestAnimationFrame(() => {
          this.hotspotPoints = this.querySelectorAll('.hotspot-point');

          this.hotspotPoints.forEach((point) => {
            point._pinned = false;

            point.addEventListener('mouseenter', (e) => this.onMouseEnter(e, point));
            point.addEventListener('mouseleave', (e) => this.onMouseLeave(e, point));
            point.addEventListener('click', (e) => this.onClick(e, point));

            // Set up tooltip portal hover handlers
            const tooltip = point.querySelector('.hotspot-point__tooltip');
            if (tooltip) {
              tooltip._point = point;
              tooltip.addEventListener('mouseenter', () => {
                if (point._closeTimeout) {
                  clearTimeout(point._closeTimeout);
                  point._closeTimeout = null;
                }
              });
              tooltip.addEventListener('mouseleave', (e) => {
                if (point._pinned) return;
                if (point.contains(e.relatedTarget)) return;
                this.closeTooltip(point);
              });
            }
          });

          document.addEventListener('click', this.boundCloseAll);
          document.addEventListener('keydown', this.boundOnKeydown);

          this.initTabs();
        });
      }

      onMouseEnter(event, point) {
        if (!this.isDesktop.matches) return;
        if (point._closeTimeout) {
          clearTimeout(point._closeTimeout);
          point._closeTimeout = null;
        }
        this.openTooltip(point);
      }

      onMouseLeave(event, point) {
        if (!this.isDesktop.matches) return;
        if (point._pinned) return;
        // Delay close to let mouse travel across the gap to the portaled tooltip
        point._closeTimeout = setTimeout(() => {
          point._closeTimeout = null;
          this.closeTooltip(point);
        }, 120);
      }

      onClick(event, point) {
        event.preventDefault();
        event.stopPropagation();

        if (point._closeTimeout) {
          clearTimeout(point._closeTimeout);
          point._closeTimeout = null;
        }

        if (this.isDesktop.matches) {
          // Desktop: click pins/unpins the tooltip
          if (point._pinned) {
            point._pinned = false;
            this.closeTooltip(point);
          } else {
            this.hotspotPoints.forEach((p) => {
              if (p !== point) {
                p._pinned = false;
                this.closeTooltip(p);
              }
            });
            point._pinned = true;
            this.openTooltip(point);
          }
        } else {
          // Mobile: toggle open/close
          const isExpanded = point.getAttribute('aria-expanded') === 'true';

          this.hotspotPoints.forEach((p) => {
            if (p !== point) this.closeTooltip(p);
          });

          if (isExpanded) {
            this.closeTooltip(point);
          } else {
            this.openTooltip(point);
          }
        }
      }

      openTooltip(point) {
        const tooltip = this._tooltipMap.get(point) || point.querySelector('.hotspot-point__tooltip');
        if (!tooltip) return;

        // Ensure tooltip is back in point for auto-position measurement
        if (tooltip.parentElement !== point) {
          this._resetTooltipStyles(tooltip);
          point.appendChild(tooltip);
        }

        if (point.dataset.tooltipPosition === 'auto') {
          this.computeAutoPosition(point, tooltip);
        }

        // Read computed position before portaling
        const position = tooltip.getAttribute('data-computed-position') ||
                         point.dataset.tooltipPosition || 'top';

        // Portal: move tooltip to hotspot-slider level to escape overflow clipping
        this._portalTooltip(point, tooltip, position);

        tooltip.removeAttribute('hidden');
        point.setAttribute('aria-expanded', 'true');

        // Clamp tooltip so it stays within the viewport
        this._clampToViewport(tooltip);
      }

      _portalTooltip(point, tooltip, position) {
        this._tooltipMap.set(point, tooltip);

        // Calculate point position relative to hotspot-slider
        const sliderRect = this.getBoundingClientRect();
        const pointRect = point.getBoundingClientRect();

        const pointCenterX = pointRect.left + pointRect.width / 2 - sliderRect.left;
        const pointCenterY = pointRect.top + pointRect.height / 2 - sliderRect.top;
        const pointTopRel = pointRect.top - sliderRect.top;
        const pointBottomRel = pointRect.bottom - sliderRect.top;
        const pointLeftRel = pointRect.left - sliderRect.left;
        const pointRightRel = pointRect.right - sliderRect.left;
        const gap = 14;

        // Remove data-computed-position so CSS rules don't conflict with inline styles
        tooltip.removeAttribute('data-computed-position');

        // Move to portal
        this._tooltipPortal.appendChild(tooltip);

        // Position absolutely relative to hotspot-slider via the portal
        tooltip.style.position = 'absolute';
        tooltip.style.zIndex = '10';
        tooltip.style.pointerEvents = 'auto';

        switch (position) {
          case 'top':
            // Place at point's top, then shift up by tooltip's own height + gap
            tooltip.style.top = `${pointTopRel}px`;
            tooltip.style.bottom = 'auto';
            tooltip.style.left = `${pointCenterX}px`;
            tooltip.style.right = 'auto';
            tooltip.style.transform = 'translate(-50%, calc(-100% - ' + gap + 'px))';
            break;
          case 'bottom':
            tooltip.style.top = `${pointBottomRel + gap}px`;
            tooltip.style.bottom = 'auto';
            tooltip.style.left = `${pointCenterX}px`;
            tooltip.style.right = 'auto';
            tooltip.style.transform = 'translateX(-50%)';
            break;
          case 'left':
            tooltip.style.top = `${pointCenterY}px`;
            tooltip.style.bottom = 'auto';
            tooltip.style.left = `${pointLeftRel}px`;
            tooltip.style.right = 'auto';
            tooltip.style.transform = 'translate(calc(-100% - ' + gap + 'px), -50%)';
            break;
          case 'right':
            tooltip.style.top = `${pointCenterY}px`;
            tooltip.style.bottom = 'auto';
            tooltip.style.left = `${pointRightRel + gap}px`;
            tooltip.style.right = 'auto';
            tooltip.style.transform = 'translateY(-50%)';
            break;
        }
      }

      _resetTooltipStyles(tooltip) {
        tooltip.style.position = '';
        tooltip.style.zIndex = '';
        tooltip.style.top = '';
        tooltip.style.bottom = '';
        tooltip.style.left = '';
        tooltip.style.right = '';
        tooltip.style.transform = '';
        tooltip.style.pointerEvents = '';
      }

      _clampToViewport(tooltip) {
        const rect = tooltip.getBoundingClientRect();
        const margin = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let dx = 0;
        let dy = 0;

        if (rect.left < margin) {
          dx = margin - rect.left;
        } else if (rect.right > vw - margin) {
          dx = (vw - margin) - rect.right;
        }

        if (rect.top < margin) {
          dy = margin - rect.top;
        } else if (rect.bottom > vh - margin) {
          dy = (vh - margin) - rect.bottom;
        }

        if (dx !== 0 || dy !== 0) {
          const currentLeft = parseFloat(tooltip.style.left) || 0;
          const currentTop = parseFloat(tooltip.style.top) || 0;
          tooltip.style.left = `${currentLeft + dx}px`;
          tooltip.style.top = `${currentTop + dy}px`;
        }
      }

      closeTooltip(point) {
        const tooltip = this._tooltipMap.get(point) || point.querySelector('.hotspot-point__tooltip');
        if (!tooltip) return;

        if (point._closeTimeout) {
          clearTimeout(point._closeTimeout);
          point._closeTimeout = null;
        }

        tooltip.setAttribute('hidden', '');
        point.setAttribute('aria-expanded', 'false');
        point._pinned = false;

        // Move tooltip back to its point
        if (tooltip.parentElement !== point) {
          this._resetTooltipStyles(tooltip);
          point.appendChild(tooltip);
        }
        this._tooltipMap.delete(point);
      }

      closeAllTooltips(event) {
        if (event && event.target.closest('.hotspot-point')) return;
        // Also ignore clicks on portaled tooltips
        if (event && event.target.closest('.hotspot-tooltip-portal')) return;
        this.hotspotPoints.forEach((point) => this.closeTooltip(point));
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.closeAllTooltips();
        }
      }

      computeAutoPosition(point, tooltip) {
        // Temporarily show tooltip (invisible) to measure its dimensions
        tooltip.removeAttribute('data-computed-position');
        tooltip.style.visibility = 'hidden';
        tooltip.removeAttribute('hidden');

        const tooltipHeight = tooltip.offsetHeight;
        const tooltipWidth = tooltip.offsetWidth;

        // Re-hide
        tooltip.setAttribute('hidden', '');
        tooltip.style.visibility = '';

        // Measure available space around the hotspot in the viewport
        const pointRect = point.getBoundingClientRect();
        const gap = 14; // ~1.2rem

        const spaceAbove = pointRect.top;
        const spaceBelow = window.innerHeight - pointRect.bottom;
        const spaceLeft = pointRect.left;
        const spaceRight = window.innerWidth - pointRect.right;

        let position = 'top';

        if (tooltipHeight + gap > spaceAbove) {
          // Not enough room above — try alternatives
          if (tooltipHeight + gap <= spaceBelow) {
            position = 'bottom';
          } else if (tooltipWidth + gap <= spaceRight) {
            position = 'right';
          } else if (tooltipWidth + gap <= spaceLeft) {
            position = 'left';
          } else {
            // Nothing fits perfectly — pick the direction with most space
            position = spaceBelow >= spaceAbove ? 'bottom' : 'top';
          }
        }

        tooltip.setAttribute('data-computed-position', position);
      }

      initTabs() {
        if (!this.hasAttribute('data-has-tabs')) return;

        this.tabButtons = this.querySelectorAll('.hotspot-tab');
        this.tabContents = this.querySelectorAll('.hotspot-tab-content');

        this.tabButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.switchTab(btn));
        });
      }

      switchTab(selectedBtn) {
        const tabIndex = selectedBtn.dataset.tabIndex;

        this.tabButtons.forEach((btn) => {
          const isSelected = btn === selectedBtn;
          btn.classList.toggle('hotspot-tab--active', isSelected);
          btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        this.tabContents.forEach((content) => {
          if (content.dataset.tabIndex === tabIndex) {
            content.removeAttribute('hidden');

            // Nudge the slideshow so it recalculates slide widths
            const slideshow = content.querySelector('slideshow-component');
            if (slideshow && slideshow.slider) {
              slideshow.slider.dispatchEvent(new Event('scroll'));
            }
          } else {
            content.setAttribute('hidden', '');
          }
        });

        this.closeAllTooltips();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundCloseAll);
        document.removeEventListener('keydown', this.boundOnKeydown);
      }
    }
  );
}

/* Theme editor integration */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:block:select', function (event) {
    /* If a tab block is selected, switch to that tab */
    const tabBtn = event.target.closest('.hotspot-tab');
    if (tabBtn) {
      const slider = tabBtn.closest('hotspot-slider');
      if (slider && slider.switchTab) {
        slider.switchTab(tabBtn);
      }
      return;
    }

    /* If a hotspot or slide is inside a tab, activate that tab first */
    const tabContent = event.target.closest('.hotspot-tab-content');
    if (tabContent) {
      const tabIndex = tabContent.dataset.tabIndex;
      const slider = tabContent.closest('hotspot-slider');
      if (slider && slider.tabButtons) {
        const matchingBtn = Array.from(slider.tabButtons).find(
          (btn) => btn.dataset.tabIndex === tabIndex
        );
        if (matchingBtn) slider.switchTab(matchingBtn);
      }
    }

    const hotspot = event.target.closest('.hotspot-point');
    if (!hotspot) return;

    const parentSlide = hotspot.closest('.slideshow__slide');
    if (!parentSlide) return;

    const parentSlideshow = parentSlide.closest('slideshow-component');
    if (parentSlideshow) {
      if (parentSlideshow.pause) parentSlideshow.pause();
      setTimeout(function () {
        parentSlideshow.slider.scrollTo({ left: parentSlide.offsetLeft });
      }, 200);
    }

    const slider = hotspot.closest('hotspot-slider');
    if (slider && slider.openTooltip) {
      slider.openTooltip(hotspot);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    const hotspot = event.target.closest('.hotspot-point');
    if (!hotspot) return;

    const slider = hotspot.closest('hotspot-slider');
    if (slider && slider.closeTooltip) {
      slider.closeTooltip(hotspot);
    }

    const parentSlideshow = hotspot.closest('slideshow-component');
    if (parentSlideshow && parentSlideshow.autoplayButtonIsSetToPlay) {
      parentSlideshow.play();
    }
  });
}
